import { StructuredDataService } from "@brookbush/structured-data";
import type {
	CertificationPreview,
	CourseAccreditation,
	CourseAccreditationByProfession,
	CourseCategory,
	CourseCategoryShallow,
	CoursePreview,
} from "@brookbush/types";

import type { GetStaticProps } from "next";
import { parseAsString, useQueryState } from "next-usequerystate";
import { useMemo } from "react";

import CoursesCatalogue from "./components/CoursesCatalogue";
import CoursesOverview from "./components/CoursesOverview";
import CoursesTutorial from "./components/CoursesTutorial";
import type { BreadcrumbItem } from "~/components/Breadcrumbs";
import { GridLayout } from "~/layouts";
import type { NextPageWithProps } from "~/types";
import fetcher from "~/utils/fetcher.client";

interface CoursesPageProps {
	shallowCategories: CourseCategoryShallow[];
	categories: CourseCategory[];
	accreditationsByProfession: CourseAccreditationByProfession[];
	certifications: CertificationPreview[];
	structuredData: string;
}

const CoursesPage: NextPageWithProps<CoursesPageProps> = ({
	shallowCategories,
	accreditationsByProfession,
	certifications,
	structuredData,
}) => {
	const [accreditationFilter] = useQueryState("accreditation", parseAsString);

	const selectedAccreditation = useMemo(
		() =>
			accreditationsByProfession
				.reduce(
					(acc: CourseAccreditation[], curr) =>
						acc.concat(...curr.accreditations),
					[],
				)
				.find((acc) => acc.slug === accreditationFilter),
		[accreditationFilter, accreditationsByProfession],
	);

	return (
		<>
			<script
				type="application/ld+json"
				id="json-ld"
				dangerouslySetInnerHTML={{
					__html: structuredData,
				}}
			/>
			<CoursesTutorial />

			<GridLayout.Sidebar className="z-20 laptop:block hidden">
				<CoursesCatalogue
					certifications={certifications}
					shallowCategories={shallowCategories}
					accreditationsByProfession={accreditationsByProfession}
				/>
			</GridLayout.Sidebar>

			<GridLayout.Body>
				<CoursesOverview
					certifications={certifications}
					shallowCategories={shallowCategories}
					accreditationsByProfession={accreditationsByProfession}
					selectedAccreditation={selectedAccreditation}
				/>
			</GridLayout.Body>
		</>
	);
};

CoursesPage.Layout = GridLayout;

export const getStaticProps: GetStaticProps<
	{},
	{ categorySlugs: string[] }
> = async () => {
	const [
		shallowCategories,
		categories,
		accreditationsByProfession,
		certifications,
		totalCredits,
	] = await Promise.all([
		fetcher("/courses/categories/shallow"),
		fetcher("/courses/categories"),
		fetcher("/courses/accreditations/by-profession?filter=true"),
		fetcher(`/certifications`),
		fetcher("/courses/credits/total"),
	]);
	const breadcrumbs: BreadcrumbItem[] = [
		{
			href: "/courses",
			name: "Courses",
		},
	];

	const courses: Array<CoursePreview> = categories.reduce(
		(accumulator: Array<CoursePreview>, category: CourseCategory) => {
			return category.courses
				? accumulator.concat(category.courses)
				: accumulator;
		},
		[],
	);

	const structuredData = await Promise.all([
		StructuredDataService.generateCourses(courses),
		StructuredDataService.getBreadcrumbs("/courses"),
	]);

	for (const result of structuredData) {
		if (!result) {
			return {
				notFound: true,
			};
		}
	}

	return {
		props: {
			shallowCategories,
			accreditationsByProfession,
			certifications,
			breadcrumbs,
			structuredData: StructuredDataService.withContext({
				"@graph": structuredData,
			}),
			fallback: {
				["/certifications"]: certifications,
				["/courses/credits/total"]: totalCredits,
				["/courses/categories"]: categories,
			},
		},
	};
};

export default CoursesPage;
